//@flow
import { SaveSettingsQuery, SaveSettingsMessage, GetSettingsResponse } from '../../autogenerated/ZoomBridge';

import HashMap from '../../protocol/HashMap';
import ErrorId from '../../protocol/ErrorId';
import GetSettings from './GetSettings';
import System from '../../basic/System';

const debug = require('../../debug/debug')('wc:queries:settings:SaveSettings');
export default class SaveSettings {
  static SETTINGS_CACHE: Map<string, any> = new Map();
  static NOTIFICATION_SETTINGS_CACHE: Map<string, any> = new Map();
  static COLLECTION_SETTINGS_CACHE: Map<string, any> = new Map();
  static TPM_PATHS_SETTINGS_CACHE;

  static VIEWPROPS = 'VIEWPROPS';
  static DEFAULT_VIEW = 'VIEW';
  static ADVANCE_SEARCH_SORTED_METADATA_CODE = 'ADVANCE_SEARCH_SORTED_METADATA_CODE';
  static ADVANCE_SEARCH_HIDE_METADATA_CODE = 'ADVANCE_SEARCH_HIDE_METADATA_CODE';
  static DISABLE_SCRUB_GENERATION = 'DISABLE_SCRUB_GENERATION';
  static FIND_SEARCH_SHOW_DELETED = 'FIND_SEARCH_SHOW_DELETED';
  static FIND_SEARCH_SHOW_EXPIRED = 'FIND_SEARCH_SHOW_EXPIRED';
  static FIND_SEARCH_SHOW_ARCHIVED = 'FIND_SEARCH_SHOW_ARCHIVED';
  static THEME = 'DESKTOP_THEME';
  static ADVANCE_SEARCH_COL = 'ADVANCE_SEARCH_COL';
  static LOG_LEVEL = 'LOG_LEVEL';

  static OPEN_ASSET_AFTER_CHECKOUT = 'OPEN_ASSET_AFTER_CHECKOUT';
  static ASK_DELETE_CONFIRMATION = 'ASK_DELETE_CONFIRMATION'; 
  static SMART_LINK_INCLUDE_IN_DELETE = 'SMART_LINK_INCLUDE_IN_DELETE';
  static FIND_ONLY_FILES = 'FIND_ONLY_FILES';
  static FIND_ONLY_FOLDERS = 'FIND_ONLY_FOLDERS';
  // static SMART_LINK_INCLUDE_IN_RESURRECT = 'SMART_LINK_INCLUDE_IN_RESURRECT';  
  

  static excludedSetting = [
    'SMART_LINK_OPERATION_ABORT_ON_FAILURE',
    'SHOW_REVISION_COMMENT',
    'ONLY_DELETE_FOR_PURGE',
    'FONT_INDEX',
    'CURRENT_LOCALE',
    'AUTO_UNLOCK_ON_DELINK',
    'DEFAULT_UPDATE_ONLY_WC_ASSETS',
    'FOLDER_MOVE_CONFIRMATION',
    'HIDE_FILES_IN_TREE',
    'FIND_ALL_REVISIONS',
    'DEFAULT_UPDATE_ONLY_WC_ASSETS',
    'LINK_CONFIRMATION_ALERT',
    'FIND_ONLY_FILES',
    // 'FIND_ONLY_FOLDERS',
    'MOVE_FILE_FOR_CONVERTWC',
    'SMART_LINK_INCLUDE_IN_RESURRECT',
    'SMART_LINK_MAKE_WRITEABLE_DEFAULT',
    'LOG_LEVEL'
  ];
  // static
  /**end */

  saveSettingsMessage: SaveSettingsMessage;

  constructor(saveSettingsMessage: SaveSettingsMessage) {
    this.saveSettingsMessage = saveSettingsMessage;
  }

  static updateSettings(): Promise<boolean> {
    return new Promise((success: (rv: boolean) => void, failure: (error: ErrorId) => void) => {
      if (debug.enabled) {
        debug('[updateSettings()] Updating settings after save.');
      }
      new GetSettings().run().then(
        (response: GetSettingsResponse) => {
          if (response) {
            response.getGeneralProps().forEach((key, value) => {
              if (!this.excludedSetting.includes(key)){
                if(key == 'OPEN_ASSET_AFTER_CHECKOUT' && !System.isDesktop()){
                  return;
                }
                else{
                 return SaveSettings.SETTINGS_CACHE.set(key, JSON.parse(value));
                }
              }
            });
            response.getNotificationProps().forEach((key, value) => {
              SaveSettings.NOTIFICATION_SETTINGS_CACHE.set(key, JSON.parse(value));
            });
            response.getCollectionProps().forEach((key, value) => {
              SaveSettings.COLLECTION_SETTINGS_CACHE.set(key, JSON.parse(value));
            });
            response.getGlobalProperties().forEach((key, value) => {
              SaveSettings.NOTIFICATION_SETTINGS_CACHE.set(key, JSON.parse(value));
            });
            SaveSettings.TPM_PATHS_SETTINGS_CACHE = response.getTpmPathsMapping();
            if (debug.enabled) {
              debug('[updateSettings()] Settings received: %o', SaveSettings.SETTINGS_CACHE);
            }
            //}
          }
          success(true);
        },
        (errorId: ErrorId) => {
          if (debug.enabled) {
            debug('[updateSettings()] error: %o', errorId);
          }
          failure(errorId);
        }
      );
    });
  }

  run(): Promise<boolean> {
    return new Promise((success: (rv: boolean) => void, failure: (error: ErrorId) => void) => {
      const query: SaveSettingsQuery = new SaveSettingsQuery();
      query.message = this.saveSettingsMessage;
      if (debug.enabled) {
        debug('[run()] Saving settings: %o', this.saveSettingsMessage);
      }
      query.run().then(
        // eslint-disable-next-line no-unused-vars
        (rv: boolean) => {
          if (debug.enabled) {
            debug('[run()] Settings saved: %o', rv);
          }
          SaveSettings.updateSettings().then(success, failure);
        },
        (errorId: ErrorId) => {
          if (debug.enabled) {
            debug('[updateSettings()] error: %o', errorId);
          }
          failure(errorId);
        }
      );
    });
  }

  static saveMultipleAsync = ({ settingMap, globalSettingMap }) => {
    const saveSettingsMsg = new SaveSettingsMessage();
    globalSettingMap.size() > 0 && saveSettingsMsg.setGlobalSettings(globalSettingMap);
    settingMap.size() > 0 && saveSettingsMsg.setSettings(settingMap);
    return new SaveSettings(saveSettingsMsg).run();
  };

  static saveAsync(key: string, value: ?string): Promise<boolean> {
    const map: HashMap = new HashMap();
    map.put(key, value);
    const isGlobalProperties = SaveSettings.NOTIFICATION_SETTINGS_CACHE.get(key)?.zmPropInfoBase?.global;
    if (isGlobalProperties) {
      return new SaveSettings(new SaveSettingsMessage().setGlobalSettings(map)).run();
    }
    return new SaveSettings(new SaveSettingsMessage().setSettings(map)).run();
  }

  static saveTPMSettings(map: ?HashMap): Promise<boolean> {
    return new SaveSettings(new SaveSettingsMessage().setTpmPaths(map)).run();
  }

  static save(key: string, value: ?string): void {
    this.saveAsync(key, value).then(
      (rv: boolean) => {
        if (debug.enabled) {
          debug('[saveAsync()] success: %o', rv);
        }
      },
      (errorId: ErrorId) => {
        if (debug.enabled) {
          debug('[updateSettings()] error: %o', errorId);
        }
      }
    );
  }

  static has(key: string): boolean {
    return (
      SaveSettings.SETTINGS_CACHE.has(key) ||
      SaveSettings.NOTIFICATION_SETTINGS_CACHE.has(key) ||
      SaveSettings.COLLECTION_SETTINGS_CACHE.has(key)
    );
  }

  static get(key: string, defaultValue: string): ?string {
    let object = SaveSettings.SETTINGS_CACHE.get(key);
    if (!object) {
      object = SaveSettings.NOTIFICATION_SETTINGS_CACHE.get(key);
    }
    if (!object) {
      object = SaveSettings.COLLECTION_SETTINGS_CACHE.get(key);
    }

    return object ? object.value : defaultValue ? defaultValue : null;
  }

  static getBoolean(key: string, defaultValue: boolean = false): boolean {
    const v = SaveSettings.get(key);
    if (!v) {
      return defaultValue;
    }
    return v === 'true' || v === '1' || v === 'on' || v === 'yes';
  }

  static getAsync(key: string, reloadFromBackend: boolean = false): Promise<?string> {
    return new Promise((success: (value: ?string) => void, failure: (error: ErrorId) => void) => {
      if (reloadFromBackend === false) {
        success(SaveSettings.get(key));
      } else {
        SaveSettings.updateSettings().then(
          () => SaveSettings.get(key),
          (error) => failure(error)
        );
      }
    });
  }
}
